import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useOverlayContext } from 'seagull';
import { AddContactModal } from '~/components/AddContactModal/AddContactModal';
import { EditContactModal } from '~/components/EditContactModal/EditContactModal';
import { useAccountingCustomers } from '~/hooks/reactQuery/queries/useAccountingCustomers';
import { useClientContacts } from '~/hooks/reactQuery/queries/useClientContacts';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { useContactArchiveRestoreDelete } from '~/hooks/useContactArchiveRestoreDelete';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { Accounting } from '~/types/api.model';
import { buildCustomerIdToDisplay, toContactCardContacts } from '../ContactsCard.utils';
export function useContactsCard(clientId) {
    const { contacts: allModels, isLoading, error, refresh } = useClientContacts(clientId, {
        refreshOnHookMount: true
    });
    const models = useMemo(()=>allModels.filter((model)=>model.isActive()), [
        allModels
    ]);
    const { showModal } = useOverlayContext();
    const { onArchive, onDelete } = useContactArchiveRestoreDelete({
        onSuccess: refresh
    });
    const showQbo = useStore(selectors.integration.showQboIntegration);
    const showXero = useStore(selectors.integration.showXeroIntegration);
    useIntegrationsSummary({
        enabled: showQbo || showXero,
        refreshOnHookMount: true
    });
    const { customers: qboCustomers } = useAccountingCustomers(Accounting.QBO, {
        enabled: showQbo,
        refreshOnHookMount: true
    });
    const { customers: xeroCustomers } = useAccountingCustomers(Accounting.Xero, {
        enabled: showXero,
        refreshOnHookMount: true
    });
    const contacts = useMemo(()=>toContactCardContacts(models, {
            showQbo,
            showXero,
            qboCustomerIdToDisplay: buildCustomerIdToDisplay(qboCustomers),
            xeroCustomerIdToDisplay: buildCustomerIdToDisplay(xeroCustomers)
        }), [
        models,
        showQbo,
        showXero,
        qboCustomers,
        xeroCustomers
    ]);
    const onAddContact = ()=>showModal({
            biName: 'client_detail_add_contact',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(AddContactModal, {
                    clientId: clientId,
                    dismissModal: dismissModal
                });
            }
        });
    const onEditContact = async (contactId)=>{
        const contact = models.find((model)=>model.id() === contactId);
        if (contact == null) return;
        await showModal({
            biName: 'client_detail_edit_contact',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(EditContactModal, {
                    clientId: clientId,
                    contact: contact,
                    dismissModal: dismissModal
                });
            }
        });
        refresh();
    };
    const withContact = (contactId, action)=>{
        const contact = models.find((model)=>model.id() === contactId);
        if (contact != null) action(contact);
    };
    return {
        contacts,
        models,
        isLoading,
        isError: error != null,
        onAddContact,
        onEditContact,
        onArchiveContact: (contactId)=>withContact(contactId, onArchive),
        onDeleteContact: (contactId)=>withContact(contactId, onDelete)
    };
}
